import React, { useEffect, useState } from 'react';
import { inventoryApi, productsApi, storesApi } from '../services/api';

export default function StockTransferPage() {
    const [products, setProducts] = useState([]);
    const [stores, setStores] = useState([]);
    const [movements, setMovements] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        product_id: '',
        quantity: '',
        from_store_id: '',
        to_store_id: '',
        reason: '',
        reference_id: '',
    });

    const load = async () => {
        const [productsRes, storesRes, movementsRes] = await Promise.all([
            productsApi.list({ limit: 500 }),
            storesApi.list({ limit: 300 }),
            inventoryApi.movements({ limit: 40 }),
        ]);

        setProducts(productsRes?.data?.data || productsRes?.data || []);
        setStores(storesRes?.data?.data || []);
        setMovements((movementsRes?.data?.data || []).filter((item) => ['transfer_out', 'transfer_in'].includes(item.movement_type)));
    };

    useEffect(() => {
        load();
    }, []);

    const storesById = Object.fromEntries(stores.map((item) => [String(item.id), item]));

    const submit = async (event) => {
        event.preventDefault();
        if (!form.product_id || !form.from_store_id || !form.to_store_id || Number(form.quantity) <= 0) {
            window.alert('Product, source, destination and positive quantity are required.');
            return;
        }
        if (String(form.from_store_id) === String(form.to_store_id)) {
            window.alert('Source and destination must be different.');
            return;
        }

        setSaving(true);
        const response = await inventoryApi.transferStock({
            product_id: Number(form.product_id),
            quantity: Number(form.quantity),
            from_store_id: Number(form.from_store_id),
            to_store_id: Number(form.to_store_id),
            reason: form.reason,
            reference_id: form.reference_id,
        });
        setSaving(false);

        if (!response.success) {
            window.alert(response.error || 'Transfer failed');
            return;
        }

        setForm({ product_id: '', quantity: '', from_store_id: '', to_store_id: '', reason: '', reference_id: '' });
        load();
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Stock Transfer</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Stock Transfer</p>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="panel stock-panel">
                    <h3>Transfer Between Store / Warehouse Locations</h3>
                    <form className="stock-form-grid" onSubmit={submit}>
                        <label>
                            <span>Product</span>
                            <select value={form.product_id} onChange={(e) => setForm((prev) => ({ ...prev, product_id: e.target.value }))} required>
                                <option value="">Select product</option>
                                {products.map((item) => (
                                    <option key={item.id} value={item.id}>{item.name} ({item.sku || 'no-sku'})</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>From (Store/Warehouse)</span>
                            <select value={form.from_store_id} onChange={(e) => setForm((prev) => ({ ...prev, from_store_id: e.target.value }))} required>
                                <option value="">Select source</option>
                                {stores.map((item) => (
                                    <option key={item.id} value={item.id}>{item.name} ({item.store_type || 'store'})</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>To (Store/Warehouse)</span>
                            <select value={form.to_store_id} onChange={(e) => setForm((prev) => ({ ...prev, to_store_id: e.target.value }))} required>
                                <option value="">Select destination</option>
                                {stores.map((item) => (
                                    <option key={item.id} value={item.id}>{item.name} ({item.store_type || 'store'})</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Quantity</span>
                            <input type="number" min="1" step="1" value={form.quantity} onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Reference</span>
                            <input value={form.reference_id} onChange={(e) => setForm((prev) => ({ ...prev, reference_id: e.target.value }))} placeholder="Transfer slip or batch ref" />
                        </label>
                        <label>
                            <span>Notes</span>
                            <input value={form.reason} onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))} placeholder="Reason for transfer" />
                        </label>
                        <div className="stock-form-actions">
                            <button className="btn" type="submit" disabled={saving}>{saving ? 'Processing...' : 'Transfer Stock'}</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <h3>Recent Transfers</h3>
                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Direction</th>
                                    <th>Product ID</th>
                                    <th>Quantity</th>
                                    <th>From</th>
                                    <th>To</th>
                                    <th>Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movements.length === 0 ? (
                                    <tr><td colSpan={7} className="dash-empty-row">No transfers yet</td></tr>
                                ) : movements.map((item) => (
                                    <tr key={item.id}>
                                        <td>{new Date(item.created_at).toLocaleString('en-NG')}</td>
                                        <td>{item.movement_type === 'transfer_out' ? 'Outgoing' : 'Incoming'}</td>
                                        <td>{item.product_id}</td>
                                        <td>{item.quantity}</td>
                                        <td>{item.from_store_id ? (storesById[String(item.from_store_id)]?.name || item.from_store_id) : '-'}</td>
                                        <td>{item.to_store_id ? (storesById[String(item.to_store_id)]?.name || item.to_store_id) : '-'}</td>
                                        <td>{item.reference_id || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
